import { useState, useEffect, useRef, useMemo } from 'react';
import BackLink from '../components/BackLink';
import useFleetStore from "../stores/useFleetStore";
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { DataTable } from '../components/table/DataTable';
import PageLayout from '../components/ui/PageLayout';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { buttonClass } from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import Field from '../components/ui/Field';
import Select from '../components/ui/Select';
import Tooltip from '../components/ui/Tooltip';

// Icons
const HashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8.39 12.648a1 1 0 0 0-.015.18c0 .305.21.508.5.508.266 0 .492-.172.555-.477l.554-2.703h1.204c.421 0 .617-.234.617-.547 0-.312-.188-.53-.617-.53h-.985l.516-2.524h1.265c.43 0 .618-.227.618-.547 0-.313-.188-.524-.618-.524h-1.046l.476-2.304a1 1 0 0 0 .016-.164.51.51 0 0 0-.516-.516.54.54 0 0 0-.539.43l-.523 2.554H7.617l.477-2.304c.008-.04.015-.118.015-.164a.51.51 0 0 0-.523-.516.54.54 0 0 0-.531.43L6.53 5.484H5.414c-.43 0-.617.22-.617.532s.187.539.617.539h.906l-.515 2.523H4.609c-.421 0-.609.219-.609.531s.188.547.61.547h.976l-.516 2.492c-.008.04-.015.125-.015.18 0 .305.21.508.5.508.265 0 .492-.172.554-.477l.555-2.703h2.242zm-1-6.109h2.266l-.515 2.563H6.859l.532-2.563z"/>
    </svg>
);

const WarningIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
    </svg>
);

// A group of items the checks flagged, with what it means. Red problems block; the rest warn.
const DiagnosticCard = ({ title, subtitle, items, renderItem, color = "red" }) => {
    if (items.length === 0) return null;

    return (
        <Alert
            tone={color === "red" ? "error" : "warning"}
            live={false}
            title={
                <>
                    {title} <span className="font-normal text-on-surface-variant tabular-nums">({items.length})</span>
                </>
            }
        >
            {subtitle && <p className="m-0 mb-2 text-xs italic">{subtitle}</p>}
            <ul className="m-0 max-h-32 list-none space-y-1 overflow-y-auto p-0">
                {items.map((item, index) => (
                    <li key={index} className="rounded-control bg-surface-container-high px-2 py-1 font-mono text-xs text-on-surface-variant">
                        {renderItem(item)}
                    </li>
                ))}
            </ul>
        </Alert>
    );
};

// The serial table's rows: the new serials from the preview, then the last 100 existing ones.
// Columns stay stable across renders; a row carries its own group.
const serialInk = (row, newInk, oldInk) => (row.original._group === "new" ? newInk : oldInk);
const SERIAL_COLUMNS = [
    {
        id: "serialnumber",
        header: "Serial Number",
        accessorFn: (r) => r.serialnumber,
        cell: ({ row }) => (
            <span className={`font-mono text-xs ${serialInk(row, "text-green-400", "text-on-surface-variant")}`}>{row.original.serialnumber}</span>
        ),
    },
    ...[
        ["modelserial", "Model"],
        ["dofmserial", "Date"],
        ["batchserial", "Batch"],
        ["dronehex", "Hex"],
        ["dronereadable", "#"],
    ].map(([id, header]) => ({
        id,
        header,
        accessorFn: (r) => r[id],
        cell: ({ row }) => <span className={`text-xs ${serialInk(row, "text-on-surface", "text-on-surface-variant")}`}>{row.original[id]}</span>,
    })),
    {
        id: "status",
        header: "Status",
        accessorFn: (r) => (r._group === "new" ? "new" : r.assigned ? "Assigned" : "Available"),
        cell: ({ row }) =>
            row.original._group === "new" ? (
                <span className="text-xs italic text-green-400">new</span>
            ) : row.original.assigned ? (
                <span className="text-xs text-primary">Assigned</span>
            ) : (
                <span className="text-xs text-on-surface-variant">Available</span>
            ),
    },
];
const getSerialRowId = (r) => r._key;
const SERIAL_GROUP_ORDER = () => ["new", "existing"];

const SerialNumberGenerator = () => {
    const api = useApi();
    const toast = useToast();
    const drones = useFleetStore((state) => state.drones);

    // state declarations with useState
    const [droneVersion, setDroneVersion] = useState("F020");
    const [selectedBatchNumber, setSelectedBatchNumber] = useState("");
    const initialYear = new Date().getFullYear().toString().substring(2);
    const [yearDOM, setYearDOM] = useState(initialYear);
    const initialMonth = (new Date().getMonth() + 1).toString().padStart(2, "0");
    const [monthDOM, setMonthDOM] = useState(initialMonth);
    const [numStart, setNumStart] = useState("1");
    const [numQty, setNumQty] = useState("1");

    const [batchDetailsArray, setBatchDetailsArray] = useState([]);
    const [manuDeets, setManuDeets] = useState([]);
    const [previewData, setPreviewData] = useState(null); // { serials, dateHex, nextStart }
    const [fullSerialDets, setFullSerialDets] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const dividerRef = useRef(null);

    // Fetch initial data on mount
    useEffect(() => {
        const fetchBatchDetails = async () => {
            await api.get(`/batchdetails`, {
                onSuccess: (batchesData) => {
                    const reversedBatches = batchesData.reverse();
                    setBatchDetailsArray(reversedBatches);
                    if (reversedBatches.length > 0) {
                        setSelectedBatchNumber(reversedBatches[0].batchserial.toString().padStart(4, "0"));
                    }
                },
                errorAlert: "Batches could not be found, please try again"
            });
        };

        const fetchManuDeets = async () => {
            await api.get(`/manufacturedetails`, {
                onSuccess: (manuData) => setManuDeets(manuData),
                errorAlert: "Manufacturing details could not be found, please try again"
            });
        };

        const fetchSerialNumbers = async () => {
            await api.get(`/serialnumbers`, {
                onSuccess: (serialData) => {
                    setFullSerialDets(serialData);
                },
                errorAlert: "Serial numbers could not be found, please try again"
            });
        };

        fetchBatchDetails();
        fetchManuDeets();
        fetchSerialNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch the next start number from backend when batch changes
    useEffect(() => {
        if (!selectedBatchNumber) return;

        api.get(`/serialnumbers/next-start/${selectedBatchNumber}`, {
            onSuccess: (data) => {
                setNumStart(String(data.nextStart));
            },
            errorAlert: "Could not fetch next serial number"
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBatchNumber]);

    // Generate and save serials atomically via backend
    const generateAndSaveSerials = async () => {
        const qty = parseInt(numQty, 10);
        if (!Number.isFinite(qty) || qty < 1) {
            toast.warning('Please enter a valid quantity');
            return;
        }

        setIsGenerating(true);

        try {
            const result = await api.post(`/serialnumbers/generate`, {
                body: {
                    modelserial: droneVersion,
                    batchserial: selectedBatchNumber,
                    month: monthDOM,
                    year: yearDOM,
                    quantity: qty
                }
            });

            if (result && result.inserted) {
                // Update fullSerialDets for diagnostics
                setFullSerialDets(prev => [...prev, ...result.inserted]);

                // Update numStart for next generation
                const lastReadable = Math.max(...result.inserted.map(s => parseInt(s.dronereadable, 10)));
                setNumStart(String(lastReadable + 1));

                // Clear preview data after successful save
                setPreviewData(null);

                // Check if we need to add manufacture details
                const dateHex = result.dateHex;
                const existingDates = manuDeets.map(d => d.dofmserial);
                if (!existingDates.includes(dateHex)) {
                    const newDofM = {
                        dofmreadable: `${yearDOM}${monthDOM}`,
                        dofmserial: dateHex,
                        batchserial: selectedBatchNumber
                    };
                    await api.post(`/manufacturedetails`, {
                        body: newDofM
                    });
                    setManuDeets(prev => [...prev, newDofM]);
                }

                toast.success(`Successfully saved ${result.inserted.length} serial numbers!`);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to generate serials. Please try again.');
        }

        setIsGenerating(false);
    };

    // Preview serials via backend API (uses same algorithm as generate)
    const previewSerials = async () => {
        const qty = parseInt(numQty, 10);
        if (!Number.isFinite(qty) || qty < 1) {
            toast.warning('Please enter a valid quantity');
            return;
        }

        setIsPreviewing(true);

        try {
            const result = await api.post(`/serialnumbers/preview`, {
                body: {
                    modelserial: droneVersion,
                    batchserial: selectedBatchNumber,
                    month: monthDOM,
                    year: yearDOM,
                    quantity: qty
                }
            });

            if (result && result.serials) {
                setPreviewData(result);
                setNumStart(String(result.nextStart));
                // Scroll to divider after render
                setTimeout(() => {
                    dividerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to generate preview. Please try again.');
        }

        setIsPreviewing(false);
    };

    // Extract serials array from preview data (for easier reference)
    // Memoize to prevent dependency issues in downstream useMemo hooks
    const serials = useMemo(() => previewData?.serials || [], [previewData?.serials]);

    // Build preview serial objects for diagnostic checks
    const previewSerialObjects = useMemo(() => {
        return serials.map((serial) => ({
            ...serial,
            isPreview: true
        }));
    }, [serials]);

    // Memoized diagnostic calculations
    const hexDuplicates = useMemo(() => {
        // Exclude batch 0000 from duplicate hex check (prototypes can reuse hex numbers)
        const existingHex = new Set(
            fullSerialDets
                .filter(serial => serial.batchserial !== '0000')
                .map(serial => serial.dronehex)
        );

        // Check preview serials against existing
        return serials
            .filter(serial => existingHex.has(serial.dronehex))
            .map(serial => ({ serial: serial.serialnumber, hex: serial.dronehex, isPreview: true }));
    }, [fullSerialDets, serials]);

    const duplicateSerials = useMemo(() => {
        const serialNumberCounts = new Map();
        const duplicates = [];
        drones.forEach(drone => {
            const sn = drone.serialnumber;
            if (sn != null) {
                serialNumberCounts.set(sn, (serialNumberCounts.get(sn) || 0) + 1);
            }
        });
        for (let [sn, count] of serialNumberCounts.entries()) {
            if (count > 1) duplicates.push({ serial: sn, isPreview: false });
        }
        return duplicates;
    }, [drones]);

    // Check if preview serials would create duplicates
    const previewSerialConflicts = useMemo(() => {
        const existingSerials = new Set(fullSerialDets.map(s => s.serialnumber));
        return serials
            .filter(serial => existingSerials.has(serial.serialnumber))
            .map(serial => ({ serial: serial.serialnumber, isPreview: true }));
    }, [fullSerialDets, serials]);

    // Check if preview dronereadable values would create duplicates
    const previewReadableConflicts = useMemo(() => {
        const existingReadables = new Set(
            fullSerialDets
                .filter(s => s.dronereadable && /^[0-9]+$/.test(s.dronereadable))
                .filter(s => selectedBatchNumber === '0000' ? s.batchserial === '0000' : s.batchserial !== '0000')
                .map(s => s.dronereadable)
        );

        return previewSerialObjects
            .filter(p => existingReadables.has(p.dronereadable))
            .map(p => ({ serial: p.serialnumber, readable: p.dronereadable, isPreview: true }));
    }, [fullSerialDets, previewSerialObjects, selectedBatchNumber]);

    const assignedOrphans = useMemo(() => {
        const droneSerialNumberSet = new Set(drones.map(drone => drone.serialnumber));
        return fullSerialDets
            .filter(serial => serial.batchserial !== '0000' && serial.assigned === true && !droneSerialNumberSet.has(serial.serialnumber))
            .map(serial => serial.serialnumber);
    }, [fullSerialDets, drones]);

    const droneOrphans = useMemo(() => {
        const serialMap = new Map(fullSerialDets.map(s => [s.serialnumber, s]));
        return drones
            .filter(drone => {
                const serialObj = serialMap.get(drone.serialnumber);
                return serialObj && serialObj.assigned === false;
            })
            .map(drone => drone.serialnumber);
    }, [fullSerialDets, drones]);

    const dronesWithoutSerials = useMemo(() => {
        return drones.filter(drone => !drone.serialnumber || drone.serialnumber === '');
    }, [drones]);

    // Preview-specific issues (blocking)
    const hasPreviewIssues = hexDuplicates.length > 0 || previewSerialConflicts.length > 0 || previewReadableConflicts.length > 0;
    // General data issues (warning)
    const hasDataIssues = duplicateSerials.length > 0 || assignedOrphans.length > 0 || droneOrphans.length > 0 || dronesWithoutSerials.length > 0;
    const hasAnyIssues = hasPreviewIssues || hasDataIssues;

    // Rows for the table: new serials (reversed to match the descending order), then the last 100 existing.
    const serialRows = useMemo(() => {
        const fresh = [...serials].reverse().map((r) => ({ ...r, _group: "new", _key: `new-${r.serialnumber}` }));
        const existing = fullSerialDets
            .filter((r) => r.dronereadable && /^[0-9]+$/.test(r.dronereadable))
            .sort((x, y) => parseInt(y.dronereadable, 10) - parseInt(x.dronereadable, 10))
            .slice(0, 100)
            .map((r) => ({ ...r, _group: "existing", _key: r.serialnumber }));
        return [...fresh, ...existing];
    }, [serials, fullSerialDets]);

    // Only the existing group gets a band, and only beside new serials: it is the divider the preview scrolls to.
    const serialGroups = useMemo(
        () => ({
            key: (r) => r._group,
            order: SERIAL_GROUP_ORDER,
            renderHeader: (key) =>
                key === "existing" && serials.length > 0 ? (
                    <span ref={dividerRef} className="block text-center text-xs font-normal text-on-surface-variant">
                        ↑ New serials above | Existing serials below ↓
                    </span>
                ) : null,
        }),
        [serials.length],
    );

    const monthNames = [
        { value: "01", label: "January" },
        { value: "02", label: "February" },
        { value: "03", label: "March" },
        { value: "04", label: "April" },
        { value: "05", label: "May" },
        { value: "06", label: "June" },
        { value: "07", label: "July" },
        { value: "08", label: "August" },
        { value: "09", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
    ];

    // Generate year options dynamically (current year + 4 previous years)
    const currentFullYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => {
        const year = currentFullYear - i;
        return { value: String(year).substring(2), label: String(year) };
    });

    return (
        <PageLayout kind="workspace" className="pt-2 md:pt-4">
            {/* Header */}
            <div className="mb-6">
                <BackLink to="/fleet" label="Drones" className="mb-2" />
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center text-primary-ink">
                            <HashIcon />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">Serial Number Generator</h1>
                            <p className="text-on-surface-variant text-sm">Generate and manage drone serial numbers</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Generator Form */}
                <div className="space-y-4">
                    <Panel title="Configuration">

                        <div className="space-y-4">
                            {/* Drone Version */}
                            <div>
                                <label className="block text-xs font-medium text-on-surface-variant mb-1.5">
                                    Drone Version
                                </label>
                                <Select
                                    label="Drone version"
                                    value={droneVersion}
                                    onChange={setDroneVersion}
                                    // Swarm's own line (D002). Unrelated to the Firefly gen-2
                                    // light show drone despite both storing generation '2'
                                    // (see inferGeneration in flasher-service.js).
                                    options={[
                                        { key: "F020", label: "F020 - Gen 2.0" },
                                        { key: "F021", label: "F021 - Gen 2.1" },
                                        { key: "D020", label: "D020 - Swarm Gen 2.0" },
                                        { key: "D021", label: "D021 - Swarm Gen 2.1" },
                                        { key: "D002", label: "D002 - Swarm Utility" },
                                    ]}
                                />
                            </div>

                            {/* Parts Batch */}
                            <div>
                                <label className="block text-xs font-medium text-on-surface-variant mb-1.5">
                                    Parts Batch
                                </label>
                                <Select
                                    label="Parts batch"
                                    value={selectedBatchNumber || null}
                                    onChange={setSelectedBatchNumber}
                                    options={batchDetailsArray.map(batch => {
                                        const serial = batch.batchserial.toString().padStart(4, '0');
                                        return { key: serial, label: `Batch ${serial}` };
                                    })}
                                />
                            </div>

                            {/* Date of Manufacture */}
                            <div>
                                <label className="block text-xs font-medium text-on-surface-variant mb-1.5">
                                    Date of Manufacture
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <Select
                                        label="Month of manufacture"
                                        value={monthDOM}
                                        onChange={setMonthDOM}
                                        options={monthNames.map(m => ({ key: m.value, label: m.label }))}
                                    />
                                    <Select
                                        label="Year of manufacture"
                                        value={yearDOM}
                                        onChange={setYearDOM}
                                        options={yearOptions.map(y => ({ key: y.value, label: y.label }))}
                                    />
                                </div>
                            </div>

                            {/* Number Start & Quantity */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-on-surface-variant mb-1.5">
                                        Number Start
                                    </label>
                                    <Tooltip label="Automatically calculated from database">
                                        <Field type="number" value={numStart} readOnly />
                                    </Tooltip>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-on-surface-variant mb-1.5">
                                        Quantity
                                    </label>
                                    <Field
                                        type="number"
                                        value={numQty}
                                        onChange={e => setNumQty(e.target.value)}
                                        min="1"
                                        max="1000"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6">
                            <button
                                className={`${buttonClass('tonal', 'md')} flex-1`}
                                onClick={previewSerials}
                                disabled={isPreviewing}
                            >
                                {isPreviewing ? 'Generating...' : 'Generate'}
                            </button>
                            <div className="flex flex-1 [&>span]:flex-1">
                                <Tooltip label={serials.length === 0 ? "Generate serials first" : hasPreviewIssues ? "Fix integrity issues first" : ""}>
                                    <button
                                        className={`${buttonClass('good', 'md')} flex-1`}
                                        onClick={generateAndSaveSerials}
                                        disabled={isGenerating || serials.length === 0 || hasPreviewIssues}
                                    >
                                        {isGenerating ? 'Saving...' : 'Save to DB'}
                                    </button>
                                </Tooltip>
                            </div>
                        </div>

                        {/* Error message when preview has issues */}
                        <Alert tone="error" variant="note" className="mt-4" show={serials.length > 0 && hasPreviewIssues}>
                            There is an issue with your serial number generation, please see Mannie.
                        </Alert>
                    </Panel>

                    {/* Warning */}
                    <Alert tone="warning" variant="note" live={false}>
                        Only use this tool if you understand the serial number format. Generated serials will be permanently stored in the database.
                    </Alert>
                </div>

                {/* Serial Number Preview Table */}
                <Panel
                    title="Serial Numbers"
                    padding="none"
                    className="overflow-hidden"
                    actions={
                        <span className="text-xs font-normal text-on-surface-variant">
                            {serials.length > 0
                                ? `${serials.length} new + last 100 existing`
                                : 'Showing last 100'}
                        </span>
                    }
                >
                    <DataTable
                        data={serialRows}
                        columns={SERIAL_COLUMNS}
                        getRowId={getSerialRowId}
                        groupBy={serialGroups}
                        on="panel"
                        density="compact"
                        maxHeight="500px"
                        empty={
                            <EmptyState variant="area">
                                <span className="flex flex-col items-center gap-2">
                                    <HashIcon />
                                    No serial numbers yet. Configure options and click Preview.
                                </span>
                            </EmptyState>
                        }
                    />
                </Panel>
            </div>

            {/* Data Integrity Section */}
            <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-lg font-bold text-white">Data Integrity Checks</h2>
                    <Appear show={!hasAnyIssues} className="flex items-center gap-1.5 text-xs text-green-400 bg-green-900/20 px-2 py-1 rounded">
                        <CheckCircleIcon />
                        All clear
                    </Appear>
                    <Appear show={hasPreviewIssues && serials.length > 0} className="flex items-center gap-1.5 text-xs text-red-400 bg-red-900/20 px-2 py-1 rounded">
                        <WarningIcon />
                        Preview has conflicts!
                    </Appear>
                </div>

                {/* Preview Checks - Always show when preview is active */}
                <Reveal show={serials.length > 0}>
                    <Panel title="Preview Validation" level={3} className="mb-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div
                                title="Checks if any of the new serial number strings already exist in the database. Each serial must be unique."
                                className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                    previewSerialConflicts.length === 0
                                        ? 'bg-green-900/20 text-green-400'
                                        : 'bg-red-900/20 text-red-400'
                                }`}
                            >
                                {previewSerialConflicts.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                                <span>Serial Numbers: {previewSerialConflicts.length === 0 ? 'PASSED' : 'FAILED'}</span>
                            </div>
                            <div
                                title="Checks if any of the new readable drone numbers (e.g., 8071, 8072) already exist. Batch 0000 has independent numbering; all other batches share a global sequence."
                                className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                    previewReadableConflicts.length === 0
                                        ? 'bg-green-900/20 text-green-400'
                                        : 'bg-red-900/20 text-red-400'
                                }`}
                            >
                                {previewReadableConflicts.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                                <span>Readable Numbers: {previewReadableConflicts.length === 0 ? 'PASSED' : 'FAILED'}</span>
                            </div>
                            <div
                                title="Checks if any of the new hex values (last 4 characters of serial) already exist in non-prototype batches. Hex values should be unique to avoid confusion during fleet operations."
                                className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                    hexDuplicates.length === 0
                                        ? 'bg-green-900/20 text-green-400'
                                        : 'bg-red-900/20 text-red-400'
                                }`}
                            >
                                {hexDuplicates.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                                <span>Hex Values: {hexDuplicates.length === 0 ? 'PASSED' : 'FAILED'}</span>
                            </div>
                        </div>
                    </Panel>
                </Reveal>

                {/* Existing Data Validation - Always show */}
                <Panel
                    level={3}
                    className={`mb-4 ${hasDataIssues ? 'ring-1 ring-danger' : ''}`}
                    title={
                        <span className={hasDataIssues ? 'text-danger' : ''}>
                            Existing Data Validation
                            {hasDataIssues && <span className="ml-2 text-xs font-normal">(Issues Found)</span>}
                        </span>
                    }
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        <div
                            title="Checks if any drones in the fleet share the same serial number."
                            className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                duplicateSerials.length === 0
                                    ? 'bg-green-900/20 text-green-400'
                                    : 'bg-danger/20 text-danger'
                            }`}
                        >
                            {duplicateSerials.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                            <span>Fleet Duplicates: {duplicateSerials.length === 0 ? 'CLEAR' : `${duplicateSerials.length} FOUND`}</span>
                        </div>
                        <div
                            title="Checks for serials marked assigned but not found in fleet (excludes batch 0000)."
                            className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                assignedOrphans.length === 0
                                    ? 'bg-green-900/20 text-green-400'
                                    : 'bg-orange-500/20 text-orange-400'
                            }`}
                        >
                            {assignedOrphans.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                            <span>Orphan Serials: {assignedOrphans.length === 0 ? 'CLEAR' : `${assignedOrphans.length} FOUND`}</span>
                        </div>
                        <div
                            title="Checks for drones with serials that aren't marked as assigned in the serial table."
                            className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                droneOrphans.length === 0
                                    ? 'bg-green-900/20 text-green-400'
                                    : 'bg-orange-500/20 text-orange-400'
                            }`}
                        >
                            {droneOrphans.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                            <span>Unassigned in Table: {droneOrphans.length === 0 ? 'CLEAR' : `${droneOrphans.length} FOUND`}</span>
                        </div>
                        <div
                            title="Checks for drones in the fleet that don't have a serial number."
                            className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-help ${
                                dronesWithoutSerials.length === 0
                                    ? 'bg-green-900/20 text-green-400'
                                    : 'bg-yellow-500/20 text-yellow-400'
                            }`}
                        >
                            {dronesWithoutSerials.length === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                            <span>Missing Serials: {dronesWithoutSerials.length === 0 ? 'CLEAR' : `${dronesWithoutSerials.length} FOUND`}</span>
                        </div>
                    </div>
                </Panel>

                {/* Detailed Issue Cards */}
                <Reveal show={hasAnyIssues}>
                    <div className="space-y-6">
                        {/* New Generation Issues */}
                        {hasPreviewIssues && (
                            <div>
                                <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
                                    <WarningIcon />
                                    New Generation Issues
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <DiagnosticCard
                                        title="Duplicate Serial Numbers"
                                        subtitle="These exact serial number strings already exist in the database."
                                        items={previewSerialConflicts}
                                        renderItem={(item) => `${item.serial} already exists!`}
                                        color="red"
                                    />
                                    <DiagnosticCard
                                        title="Duplicate Readable Numbers"
                                        subtitle="These drone numbers are already in use. Check if Number Start is correct."
                                        items={previewReadableConflicts}
                                        renderItem={(item) => `#${item.readable} → ${item.serial}`}
                                        color="red"
                                    />
                                    <DiagnosticCard
                                        title="Duplicate Hex Values"
                                        subtitle="These hex identifiers already exist in non-prototype batches, which could cause confusion during operations."
                                        items={hexDuplicates}
                                        renderItem={(item) => `${item.hex} (${item.serial})`}
                                        color="red"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Existing Data Issues */}
                        {hasDataIssues && (
                            <div>
                                <h3 className="text-sm font-semibold text-orange-400 mb-3 flex items-center gap-2">
                                    <WarningIcon />
                                    Existing Data Issues
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <DiagnosticCard
                                        title="Duplicate Serial Numbers in Fleet"
                                        subtitle="Multiple drones in the fleet have the same serial number assigned. Each drone should have a unique serial."
                                        items={duplicateSerials}
                                        renderItem={(item) => item.serial}
                                        color="orange"
                                    />
                                    <DiagnosticCard
                                        title="Assigned in Serial Table, Missing from Fleet"
                                        subtitle="This might just mean these serial numbers were generated, but we moved on to a new batch before they were assigned."
                                        items={assignedOrphans}
                                        renderItem={(sn) => sn}
                                        color="orange"
                                    />
                                    <DiagnosticCard
                                        title="In Fleet but Unassigned in Serial Table"
                                        subtitle="These drones have a serial number, but the serial table shows them as 'unassigned'. The serial table may need to be updated."
                                        items={droneOrphans}
                                        renderItem={(sn) => sn}
                                        color="orange"
                                    />
                                    <DiagnosticCard
                                        title="Drones Without Serial Numbers"
                                        subtitle="These drones exist in the fleet but don't have a serial number assigned yet. They may be newly added or imported."
                                        items={dronesWithoutSerials}
                                        renderItem={(drone) => `${drone.title || 'Unknown'} - Port: ${drone.hostport || 'N/A'}`}
                                        color="yellow"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Reveal>
            </div>
        </PageLayout>
    );
};

export default SerialNumberGenerator;
